import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Loader2 } from "lucide-react";
import { createContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import OrnamentalDivider from "@/components/OrnamentalDivider";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message required");
      return;
    }
    setBusy(true);
    try {
      await createContact(form);
      setSent(true);
      toast.success("Message sent to Martin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12">
      <div>
        <div className="stamp">Contact</div>
        <h1 className="serif text-5xl md:text-6xl text-[#6E2638] mt-4">Say hello to Martin.</h1>
        <OrnamentalDivider />
        <p className="text-[#1A1817]/85 leading-relaxed">
          Booking questions, custom walk ideas, feedback, or you just want to argue about the
          best tram route in town? Send a note — Martin reads and replies to everything himself.
        </p>
        <div className="mt-8 space-y-4">
          <a href="mailto:Mmmwt35@gmail.com" className="flex items-center gap-3 text-[#6E2638] serif text-xl" data-testid="contact-mailto">
            <Mail /> Mmmwt35@gmail.com
          </a>
          <div className="flex items-center gap-3 text-[#1E4637]">
            <MapPin /> Meet at Federation Square · in front of the big screen
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-warm rounded-2xl p-8" data-testid="contact-form">
        {sent ? (
          <div className="text-center py-10" data-testid="contact-sent">
            <div className="serif text-3xl text-[#6E2638]">Thanks — got it.</div>
            <p className="text-[#1A1817]/80 mt-3">Martin will reply to {form.email} soon.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="c-name" required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="c-email" required />
              </div>
            </div>
            <div className="mt-5">
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => update("subject", e.target.value)} data-testid="c-subject" />
            </div>
            <div className="mt-5">
              <Label>Message</Label>
              <Textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="c-message" required />
            </div>
            <button className="btn-primary mt-8 w-full justify-center" disabled={busy} data-testid="c-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send message"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
